import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useFormContext } from "react-hook-form";
import MetaAgeDemographicInsightsCard from "./MetaAgeDemographicInsightsCard";
import MetaGenderDemographicInsightsCard from "./MetaGenderDemographicInsightsCard";
import MetaPublisherPlatformInsightsCard from "./MetaPublisherPlatformInsightsCard";
import {
  filterAgeDemographicInsightsRows,
  filterGenderInsightsRowsWithoutUnknown,
  sortPublisherPlatformInsightsRows,
} from "./MetaDemographicInsightsTables";
import { getFromStatisticsAdsCampaign } from "../../../../../services/adsCampaigns";
import { META } from "../../../../../utils/constants";
import {
  VALUE_NUMBER,
  VALUE_WITH_PERCENTAGE,
  ADS_CAMPAIGNS_STATISTICS_INSIGHTS_BEST_PERFORMING_AD_GROUP,
  ADS_CAMPAIGNS_STATISTICS_INSIGHTS_BEST_PERFORMING_AD,
  IMPRESSIONS,
  RESULTS,
  CTR,
  CLICKS,
  AGE_RANGE_PLACEHOLDER,
  GENDERS_PLACEHOLDER,
  FEMALES,
  MALES,
  LOCATIONS_PLACEHOLDER,
  INTERESTS_PLACEHOLDER,
  DEMOGRAPHICS_PLACEHOLDER,
  BEHAVIORS_PLACEHOLDER,
  CUSTOM_AUDIENCES,
  CREATIVE,
  ROAS,
} from "../../../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../../../lib/i18next";
import { openLink } from "../../../../../utils/string";
import Grid from "@mui/material/Grid";
import CardMedia from "../../../../../components/Media/CardMedia";
import _ from "lodash";
import PerformingAssetsCard from "../components/PerformingAssetsCard";
import ModalMedia from "../../../../../components/Modal/ModalMedia";
import { useState } from "react";

const MetaPerformingAssets = () => {
  const { t } = useTranslationApp();
  const { campaign, campaignID, getCostByMainCurrency } = useFormContext();
  const { statistics } = getFromStatisticsAdsCampaign(campaign);
  const demographicInsights = statistics[META]?.campaign?.demographicInsights;
  const placementInsights = statistics[META]?.campaign?.placementInsights;

  // This function selects the best ad group based on mainAction if available, otherwise by clicks
  const getBestAdGroup = (adGroupsStatistics) => {
    if (!adGroupsStatistics) return null;

    let bestByMainAction = null;
    let bestByClick = null;
    let hasMainAction = false;

    Object.entries(adGroupsStatistics).forEach(([adGroupId, stats]) => {
      // Check if this ad group has mainAction
      if (stats.mainAction && stats.mainAction > 0) {
        hasMainAction = true;
        if (
          !bestByMainAction ||
          stats.mainAction > bestByMainAction.statistics.mainAction
        ) {
          bestByMainAction = {
            adGroupId,
            statistics: stats,
          };
        }
      }
      // Always check for best by clicks as fallback
      if (
        !bestByClick ||
        (stats.click || 0) > (bestByClick.statistics.click || 0)
      ) {
        bestByClick = {
          adGroupId,
          statistics: stats,
        };
      }
    });

    // If at least one ad group has mainAction, return the best by mainAction, else by clicks
    return hasMainAction ? bestByMainAction : bestByClick;
  };

  // This function selects the best ad based on mainAction if available, otherwise by clicks
  const getBestAd = (adsGroups, statistics) => {
    if (!adsGroups || !statistics) return null;

    let bestAdByMainAction = null;
    let bestAdByClicks = null;
    let hasMainAction = false;

    adsGroups.forEach((adGroup) => {
      if (adGroup.platform !== META) return;

      adGroup.ads.forEach((ad) => {
        const adStats = statistics[ad.id];
        if (!adStats) return;

        // Check if this ad has mainAction
        if (adStats.mainAction && adStats.mainAction > 0) {
          hasMainAction = true;
          if (
            !bestAdByMainAction ||
            adStats.mainAction > bestAdByMainAction.statistics.mainAction
          ) {
            bestAdByMainAction = {
              adGroupId: adGroup.id,
              adId: ad.id,
              statistics: adStats,
            };
          }
        }

        // Always check for best by clicks as fallback
        if (
          !bestAdByClicks ||
          (adStats.click || 0) > (bestAdByClicks.statistics.click || 0)
        ) {
          bestAdByClicks = {
            adGroupId: adGroup.id,
            adId: ad.id,
            statistics: adStats,
          };
        }
      });
    });
    // If at least one ad has mainAction, return the best by mainAction, else by clicks
    return hasMainAction ? bestAdByMainAction : bestAdByClicks;
  };

  const bestAdGroup = getBestAdGroup(statistics[META].adsGroups);
  const bestAd = getBestAd(campaign.adsGroups, statistics[META].ads);

  const getAdGroupByID = (id) => {
    if (!id) {
      return {};
    }
    const adGroupMeta = campaign.adsGroups.filter(
      (adGroup) => adGroup.platform === META
    );
    const adGroup = adGroupMeta.find((adGroup) => adGroup.id === id);

    return { adGroup, adGroupID: adGroup.id };
  };

  const getAdByID = (adId) => {
    if (!adId) {
      return {};
    }
    const adGroupsMeta = campaign.adsGroups.filter(
      (adGroup) => adGroup.platform === META
    );

    const foundAdGroup = adGroupsMeta.find((adGroup) =>
      adGroup.ads.some((ad) => ad.id === adId)
    );

    if (!foundAdGroup) {
      return {};
    }

    const ad = foundAdGroup.ads.find((ad) => ad.id === adId);
    const adGroupIndex = adGroupsMeta.findIndex(
      (group) => group.id === foundAdGroup.id
    );
    const adGroupID = foundAdGroup.id;

    return {
      ad,
      adID: ad.id,
      adGroupIndex,
      adGroupID,
    };
  };

  const { adGroup = {}, adGroupID } = getAdGroupByID(bestAdGroup?.adGroupId);
  const { ad = {}, adID, adGroupID: adGroupIDAd } = getAdByID(bestAd?.adId);

  const demographicAgeRows = filterAgeDemographicInsightsRows(
    demographicInsights?.age
  );
  const demographicGenderRows = filterGenderInsightsRowsWithoutUnknown(
    demographicInsights?.gender
  );
  const publisherPlatformRows = sortPublisherPlatformInsightsRows(
    placementInsights?.publisherPlatform
  );

  const hasAudienceDemographicData =
    demographicAgeRows.length > 0 || demographicGenderRows.length > 0;
  const hasPublisherPlatformData = publisherPlatformRows.length > 0;

  const bestAdGroupStats = !_.isEmpty(bestAdGroup)
    ? [
        {
          label: t(IMPRESSIONS),
          value: t(VALUE_NUMBER, { value: bestAdGroup.statistics.impressions }),
        },
        {
          label: t(CLICKS),
          value: t(VALUE_NUMBER, { value: bestAdGroup.statistics.click }),
        },
        {
          label: t(CTR),
          value: t(VALUE_WITH_PERCENTAGE, {
            value: bestAdGroup.statistics.ctr,
          }),
        },
        {
          label: t(RESULTS),
          value: t(VALUE_NUMBER, { value: bestAdGroup.statistics.mainAction }),
        },
      ]
    : [];

  const bestAdStats = !_.isEmpty(bestAd)
    ? [
        {
          label: t(IMPRESSIONS),
          value: t(VALUE_NUMBER, { value: bestAd.statistics.impressions }),
        },
        {
          label: t(CLICKS),
          value: t(VALUE_NUMBER, { value: bestAd.statistics.click }),
        },
        {
          label: t(CTR),
          value: t(VALUE_WITH_PERCENTAGE, { value: bestAd.statistics.ctr }),
        },
        {
          label: t(RESULTS),
          value: t(VALUE_NUMBER, { value: bestAd.statistics.mainAction }),
        },
      ]
    : [];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 2,
          flexWrap: "nowrap",
          width: "100%",
        }}
      >
        {!_.isEmpty(bestAdGroup) && (
          <PerformingAssetsCard
            platform={META}
            title={t(
              ADS_CAMPAIGNS_STATISTICS_INSIGHTS_BEST_PERFORMING_AD_GROUP
            )}
            subtitle={adGroup.name}
            onSubtitleClick={() =>
              openLink(
                `ads-campaign?campaignID=${campaignID}&platform=${META}&adGroupID=${adGroupID}`
              )
            }
            statistics={
              bestAdGroup.statistics.roas
                ? [
                    ...bestAdGroupStats,
                    {
                      label: t(ROAS),
                      value: t(VALUE_NUMBER, {
                        value: bestAdGroup.statistics.roas,
                      }),
                    },
                  ]
                : bestAdGroupStats
            }
            previousContent={<BestAdGroupSegmentation adGroup={adGroup} />}
            sx={{
              flex: 1,
              minWidth: 0,
            }}
          />
        )}

        {!_.isEmpty(bestAd) && (
          <PerformingAssetsCard
            platform={META}
            title={t(ADS_CAMPAIGNS_STATISTICS_INSIGHTS_BEST_PERFORMING_AD)}
            subtitle={ad.name}
            onSubtitleClick={() =>
              openLink(
                `ads-campaign?campaignID=${campaignID}&platform=${META}&adGroupID=${adGroupIDAd}&adID=${adID}`
              )
            }
            statistics={bestAdStats}
            nextContent={<BestAdCreative ad={ad} />}
            sx={{
              flex: 1,
              minWidth: 0,
            }}
          />
        )}
      </Box>

      {hasAudienceDemographicData && (
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            width: "100%",
          }}
        >
          <MetaAgeDemographicInsightsCard
            ageRows={demographicAgeRows}
            getCostByMainCurrency={getCostByMainCurrency}
            sx={{
              flex: 1,
              minWidth: { xs: "100%", sm: 280 },
            }}
          />
          <MetaGenderDemographicInsightsCard
            genderRows={demographicGenderRows}
            getCostByMainCurrency={getCostByMainCurrency}
            sx={{
              flex: 1,
              minWidth: { xs: "100%", sm: 280 },
            }}
          />
        </Box>
      )}

      {hasPublisherPlatformData && (
        <MetaPublisherPlatformInsightsCard
          publisherPlatformRows={publisherPlatformRows}
          getCostByMainCurrency={getCostByMainCurrency}
          sx={{
            width: { xs: "100%", sm: "50%" },
          }}
        />
      )}
    </Box>
  );
};

const SEGMENTATION_FIELDS = [
  "ageRange",
  "genders",
  "locations",
  "interests",
  "demographics",
  "behaviors",
  "customAudiences",
];
const BestAdGroupSegmentation = ({ adGroup = {} }) => {
  const { t } = useTranslationApp();

  const getSegmentationFields = () => {
    const segmentationFieldsData = [];

    SEGMENTATION_FIELDS.forEach((field) => {
      const fieldData = adGroup[field];
      if (!fieldData || fieldData?.length === 0) return;
      if (field === "ageRange") {
        const [minAge, maxAge] = fieldData || [];
        segmentationFieldsData.push({
          label: t(AGE_RANGE_PLACEHOLDER),
          value: `${minAge}-${maxAge}`,
        });
      } else if (field === "genders") {
        const genders = fieldData || {};
        segmentationFieldsData.push({
          label: t(GENDERS_PLACEHOLDER),
          value: Object.keys(genders)
            .filter((gender) => genders[gender] === true)
            .map((gender) => t(gender === "male" ? MALES : FEMALES))
            .join(", "),
        });
      } else {
        const value = fieldData.map((item) => item.name).join(", ");
        let label = "";
        switch (field) {
          case "locations":
            label = t(LOCATIONS_PLACEHOLDER);
            break;
          case "interests":
            label = t(INTERESTS_PLACEHOLDER);
            break;
          case "demographics":
            label = t(DEMOGRAPHICS_PLACEHOLDER);
            break;
          case "behaviors":
            label = t(BEHAVIORS_PLACEHOLDER);
            break;
          case "customAudiences":
            label = t(CUSTOM_AUDIENCES);
            break;
        }

        segmentationFieldsData.push({
          label,
          value,
        });
      }
    });

    return segmentationFieldsData;
  };

  const segmentationFields = getSegmentationFields();

  return (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      {segmentationFields.map((field) => (
        <Grid item xs={6} key={field.label}>
          <Box>
            <Typography
              variant="subtitle1"
              color="fields.placeholder"
              component="p"
            >
              {field.label}
            </Typography>
            <Typography variant="body1">{field.value}</Typography>
          </Box>
        </Grid>
      ))}
    </Grid>
  );
};

const BestAdCreative = ({ ad = {} }) => {
  const { t } = useTranslationApp();
  const [previewMediaUrl, setPreviewMediaUrl] = useState(null);

  const firstMediaUrl = ad?.mediaUrls?.[0];

  if (!firstMediaUrl) return null;

  const onMediaClick = () => {
    setPreviewMediaUrl(firstMediaUrl);
  };

  const onClosePreview = () => {
    setPreviewMediaUrl(null);
  };

  return (
    <>
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle1" color="fields.placeholder">
          {t(CREATIVE)}
        </Typography>
        <Box
          onClick={onMediaClick}
          sx={{
            cursor: "pointer",
          }}
        >
          <CardMedia
            url={firstMediaUrl}
            sx={{
              width: "auto",
              height: 250,
              objectFit: "contain",
            }}
          />
        </Box>
      </Box>

      {previewMediaUrl && (
        <ModalMedia
          modalOpen={!!previewMediaUrl}
          onCloseModal={onClosePreview}
          url={previewMediaUrl}
          showEditButton={false}
          showShareButton={false}
          showDownloadButton={false}
        />
      )}
    </>
  );
};

export default MetaPerformingAssets;
